import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";

import { useEmbarcacaoAcomodacoes } from "@/modules/embarcacoes";
import type {
  EmbarcacaoAcomodacoes,
  EmbarcacaoAcomodacaoTipo,
  EmbarcacaoAcomodacoesField,
} from "@/modules/embarcacoes";
import type { EmptyStateContent } from "@/screens/control-panel/components/empty-state";
import {
  DetailField,
  DetailSection,
  DetailSectionFields,
  DetailSectionState,
} from "../detail-section";
import {
  EMPTY_FIELD_TEXT,
  formatDetailDate,
  formatDetailNumber,
} from "../embarcacao-detail-format";
import type { EmbarcacaoDetailSectionProps } from "../embarcacao-detail-section";
import styles from "./acomodacoes-section.module.scss";

export const ACOMODACOES_ID = "acomodacoes";
export const ACOMODACOES_TITLE = "Acomodações";
export const ACOMODACOES_LOADING_MESSAGE = "Carregando as Acomodações da embarcação...";

/** Padrão global de estado vazio do Hub para a seção sem informações (AC4). */
const ACOMODACOES_EMPTY_STATE: EmptyStateContent = {
  title: "Nenhuma informação de Acomodações disponível",
  description:
    "Assim que houver dados de acomodação para esta embarcação, eles aparecerão nesta seção.",
};

const TEST_ID = "embarcacao-acomodacoes";

interface AcomodacoesGeneralFieldDefinition {
  readonly key: EmbarcacaoAcomodacoesField;
  readonly label: string;
  /** Tratamento do valor textual; hoje só a data de atualização da acomodação. */
  readonly format?: (value: string) => string;
}

/** Os campos gerais da seção, na ordem do AC2. A data da seção vai no cabeçalho. */
export const ACOMODACOES_FIELDS: AcomodacoesGeneralFieldDefinition[] = [
  { key: "possuiEnfermaria", label: "Possui enfermaria?" },
  { key: "possuiAcademia", label: "Possui academia?" },
  { key: "atualizacaoAcomodacao", label: "Data Atualização Acomodações", format: formatDetailDate },
];

interface AcomodacoesColumnDefinition {
  readonly field: keyof EmbarcacaoAcomodacaoTipo;
  readonly header: string;
  /** Coluna numérica: aplica o idioma da tela sem arredondar (AC3). */
  readonly numeric?: boolean;
}

/** As 4 colunas da tabela, na ordem do AC3. */
export const ACOMODACOES_COLUMNS: AcomodacoesColumnDefinition[] = [
  { field: "tipo", header: "Tipo de acomodação" },
  { field: "quantidade", header: "Quantidade de acomodações", numeric: true },
  { field: "quantoComporta", header: "Quanto comporta", numeric: true },
  { field: "comentario", header: "Comentário" },
];

/** Valor do campo geral pronto para a tela; `null` segue como campo sem preenchimento. */
function generalFieldValue(
  acomodacoes: EmbarcacaoAcomodacoes,
  { key, format }: AcomodacoesGeneralFieldDefinition,
): string | null {
  const value = acomodacoes[key];
  if (typeof value !== "string") return null;

  return format ? format(value) : value;
}

/** Célula da tabela: número no idioma da tela ou o texto sem preenchimento (AC3/AC4). */
function cellContent(tipo: EmbarcacaoAcomodacaoTipo, column: AcomodacoesColumnDefinition): string {
  const value = tipo[column.field];
  if (value === null) return EMPTY_FIELD_TEXT;

  return typeof value === "number" ? formatDetailNumber(value) : value;
}

/**
 * Seção Acomodações do detalhamento da embarcação (US-1797).
 *
 * Estruturada como as demais seções — título, data de última atualização no
 * cabeçalho e estados de carregamento/exceção compartilhados —, mas é a
 * primeira a trazer uma tabela: os campos gerais aparecem acima e a lista de
 * tipos de acomodação, uma linha por tipo (AC3), logo abaixo. Exclusivamente
 * de leitura: nenhum botão ou campo de cadastro, edição ou exclusão, e a tabela
 * não tem ordenação, paginação, filtros nem ajuste de colunas (AC1/AC3).
 */
export function AcomodacoesSection({
  embarcacaoId,
  onReauthenticate,
}: EmbarcacaoDetailSectionProps) {
  const { status, acomodacoes, errorMessage, reload } = useEmbarcacaoAcomodacoes(embarcacaoId);

  return (
    <DetailSection
      id={ACOMODACOES_ID}
      title={ACOMODACOES_TITLE}
      // Só há data de atualização a exibir quando a seção tem dados.
      ultimaAtualizacao={acomodacoes ? acomodacoes.ultimaAtualizacao : undefined}
      testId={TEST_ID}
    >
      {acomodacoes ? (
        <div className={styles.body}>
          <DetailSectionFields testId={`${TEST_ID}-fields`}>
            {ACOMODACOES_FIELDS.map((field) => (
              <DetailField
                key={field.key}
                label={field.label}
                value={generalFieldValue(acomodacoes, field)}
                testId={`${TEST_ID}-${field.key}`}
              />
            ))}
          </DetailSectionFields>

          {acomodacoes.acomodacoes.length > 0 ? (
            <div className={styles.tableWrapper}>
              <DataTable
                className={styles.table}
                value={acomodacoes.acomodacoes}
                // Rola na horizontal quando as colunas não cabem na largura (AC5).
                tableStyle={{ minWidth: "40rem" }}
                // Somente leitura: sem ordenação, paginação, filtros ou seleção (AC3).
                aria-label={ACOMODACOES_TITLE}
                data-testid={`${TEST_ID}-table`}
              >
                {ACOMODACOES_COLUMNS.map((column) => (
                  <Column
                    key={column.field}
                    header={column.header}
                    body={(tipo: EmbarcacaoAcomodacaoTipo) => cellContent(tipo, column)}
                  />
                ))}
              </DataTable>
            </div>
          ) : null}
        </div>
      ) : (
        <DetailSectionState
          // `success` sempre traz os campos: aqui só chegam carregamento e exceções.
          status={status === "success" ? "error" : status}
          loadingMessage={ACOMODACOES_LOADING_MESSAGE}
          errorMessage={errorMessage}
          emptyContent={ACOMODACOES_EMPTY_STATE}
          onReauthenticate={onReauthenticate}
          onRetry={reload}
          testId={TEST_ID}
        />
      )}
    </DetailSection>
  );
}
